import { useLocation } from "react-router-dom";
import qs from "qs";
import { formatDate } from "../../utils/DateUtils";
import logo from "../../resources/logo.png";
import { useEffect } from "react";
import { formatCurrency } from "../../utils/CurrencyUtils";

const Receipt = () => {
    const location = useLocation();

    const queryParams = qs.parse(location.search, { ignoreQueryPrefix: true });
    const client =
        typeof queryParams.clientInfo === "string"
            ? JSON.parse(queryParams.clientInfo)
            : queryParams.clientInfo;
    const date = queryParams.date;
    const customer =
        typeof queryParams.customer === "string"
            ? JSON.parse(queryParams.customer)
            : queryParams.customer;
    const grade =
        typeof queryParams.grade === "string"
            ? JSON.parse(queryParams.grade)
            : queryParams.grade;
    const rawCredit =
        typeof queryParams.currentCredit === "string"
            ? JSON.parse(queryParams.currentCredit)
            : queryParams.currentCredit;
    const selectedProducts =
        typeof queryParams.selectedProducts === "string"
            ? JSON.parse(queryParams.selectedProducts)
            : queryParams.selectedProducts || [];
    const transactions = Array.isArray(queryParams.transactions)
        ? queryParams.transactions
        : [queryParams.transactions];
    const id = queryParams.orderId || "";
    const totalAmount = parseFloat(queryParams.totalAmount);
    const change = parseFloat(queryParams.change);
    const operator = queryParams.operator;
    const print =
        queryParams.print !== undefined && queryParams.print !== "false";
    const auto_print =
        queryParams.auto_print !== undefined &&
        queryParams.auto_print !== "false";

    const isPayingStudent =
        queryParams.isPayingStudent === true ||
        queryParams.isPayingStudent === "true";

    const creditUsed = transactions
        .filter((t) => t.paymentMethod === "CREDITO FOODZ")
        .reduce((acc, t) => acc + parseFloat(t.received_value || 0), 0);

    const credit = isPayingStudent ? rawCredit - creditUsed : rawCredit;

    useEffect(() => {
        if (auto_print) {
            const timer = setTimeout(() => {
                window.print();
            }, 1000);

            return () => clearTimeout(timer);
        }
    }, [auto_print]);

    const handleCopy = () => {
        const lines = [];

        lines.push(`*${client.client_name.toUpperCase()}*`);
        lines.push(client.address);
        lines.push(`Fone: ${client.phone_number}`);
        lines.push(`CNPJ: ${client.cnpj}`);
        lines.push("-----------------------------");
        lines.push(
            `Impressão em: ${new Date().toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })} ${new Date().toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo' })}`
        );
        lines.push("-----------------------------");
        if (date) {
            lines.push(`Data da compra: ${formatDate(date)}`);
        }

        lines.push("-----------------------------");
        lines.push(`*CUPOM FISCAL*`);
        lines.push(`Pedido N.º: ${id}`);
        lines.push(`Cliente: ${customer}`);
        lines.push(`Série: ${grade}`);
        lines.push("-----------------------------");
        lines.push(`*ITENS*`);
        selectedProducts.forEach((item, index) => {
            lines.push(`${String(index + 1).padStart(2, "0")} - ${item.name}`);
            lines.push(
                `    Qtde: ${item.quantity}  |  Vlr: ${item.price.toLocaleString(
                    "pt-BR",
                    { style: "currency", currency: "BRL" }
                )}`
            );
        });

        lines.push("-----------------------------");
        lines.push(`*PAGAMENTO*`);
        transactions.forEach((transaction) => {
            lines.push(
                `${transaction.paymentMethod}: ${parseFloat(
                    transaction.received_value
                ).toLocaleString("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                })}`
            );
        });

        lines.push(
            `Troco: ${change.toLocaleString("pt-BR", {
                style: "currency",
                currency: "BRL",
            })}`
        );
        lines.push(
            `*TOTAL:* ${totalAmount.toLocaleString("pt-BR", {
                style: "currency",
                currency: "BRL",
            })}`
        );
        lines.push("-----------------------------");

        // 🔧 usar o mesmo critério da view
        const effectiveCredit = print ? credit : rawCredit;
        const label =
            effectiveCredit < 0 ? "*SALDO DEVEDOR:*" : "*SALDO ATUAL DO ALUNO:*";
        lines.push(`${label} ${formatCurrency(effectiveCredit)}`);

        lines.push("-----------------------------");
        lines.push(`Operador: ${operator}`);
        lines.push("-----------------------------");

        const receiptText = lines.join("\n");
        navigator.clipboard.writeText(receiptText);
    };

    return (
        <div className="w-full h-full p-4 bg-white text-sm font-mono">
            <div className="print:hidden mb-4 flex justify-center items-center">
                <button
                    id="copy-button"
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                    onClick={handleCopy}
                >
                    Copiar Recibo
                </button>
            </div>

            <div id="content">
                <div className="mb-4">
                    <p className="font-bold uppercase text-center">
                        {client.client_name}
                    </p>
                    <p className="text-center">{client.address}</p>
                    <p className="text-center">{client.phone_number}</p>
                    <p className="text-center">CNPJ: {client.cnpj}</p>
                </div>

                <div className="flex justify-between items-center font-bold border-t border-dashed border-gray-300 py-2">
                    <p>IMPRESSO EM</p>
                    <p>
                        {new Date().toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}{" "}
                        {new Date().toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                    </p>
                </div>

                {date && (
                    <div className="flex justify-between items-center font-bold border-t border-dashed border-gray-300 py-2">
                        <p>DATA DA COMPRA</p>
                        <p>{formatDate(date)}</p>
                    </div>
                )}

                <div className="border-t border-b border-dashed border-gray-300">
                    <p className="font-bold text-center my-2">CUPOM FISCAL</p>
                    <p className="text-center my-2">Pedido N.: {id}</p>
                    <p className="text-center mb-2">Cliente: {customer}</p>
                    <p className="text-center mb-2">Série: {grade}</p>
                    <div className="flex justify-between mb-1">
                        <span>ITEM</span>
                        <span>NOME</span>
                        <span>QTDE</span>
                        <span>VALOR UN.</span>
                    </div>
                </div>

                <div className="mb-4">
                    <ul className="overflow-y-auto max-h-40 list-none p-0">
                        {selectedProducts.length > 0 ? (
                            selectedProducts.map((item, index) => (
                                <li
                                    key={index}
                                    className="product-item flex justify-between mb-1"
                                >
                                    <span>{String(index + 1).padStart(3, "0")}</span>
                                    <span>{item.name}</span>
                                    <span>{item.quantity}</span>
                                    <span>
                                        {item.price.toLocaleString("pt-br", {
                                            style: "currency",
                                            currency: "BRL",
                                        })}
                                    </span>
                                </li>
                            ))
                        ) : (
                            <li className="text-gray-500">
                                Nenhum item selecionado
                            </li>
                        )}
                    </ul>
                </div>

                <div className="border-t border-b border-dashed border-gray-300 my-2">
                    {transactions.map((transaction, index) => (
                        <div key={index} className="my-2">
                            <p className="flex justify-between">
                                <span>MÉTODO DE PAGAMENTO</span>
                                <span>{transaction.paymentMethod}</span>
                            </p>
                            <p className="flex justify-between">
                                <span>VALOR RECEBIDO</span>
                                <span>
                                    {parseFloat(
                                        transaction.received_value
                                    ).toLocaleString("pt-br", {
                                        style: "currency",
                                        currency: "BRL",
                                    })}
                                </span>
                            </p>
                        </div>
                    ))}
                    <p className="flex justify-between">
                        <span>TROCO</span>
                        <span>
                            {parseFloat(change).toLocaleString("pt-br", {
                                style: "currency",
                                currency: "BRL",
                            })}
                        </span>
                    </p>
                    <p className="font-bold text-right text-lg flex justify-between">
                        <span>TOTAL</span>
                        <span>
                            {totalAmount.toLocaleString("pt-br", {
                                style: "currency",
                                currency: "BRL",
                            })}
                        </span>
                    </p>

                    {/* 🔧 Usa mesmo critério da cópia */}
                    <div className="font-bold border-t border-dashed border-gray-300 py-2">
                        <p className="flex justify-between">
                            <span>
                                {credit < 0
                                    ? "SALDO DEVEDOR"
                                    : "SALDO ATUAL DO ALUNO"}
                            </span>
                            <span>{formatCurrency(credit)}</span>
                        </p>
                    </div>
                </div>

                <div>
                    <p className="flex justify-center items-center font-bold py-2">
                        <span>OPERADOR:</span>
                        <span>{operator}</span>
                    </p>
                    <img
                        src={logo}
                        alt="Logo Foodz"
                        className="w-2/5 justify-self-center"
                    />
                </div>
            </div>
        </div>
    );
};

export default Receipt;
